import { useNavigate } from 'react-router-dom';
import { Emblem } from '../../components/Emblem';
import { Stripe } from '../../components/Stripe';
import { Shirt } from '../../components/Shirt';
import { ProductGallery } from '../../components/ProductGallery';
import { availableSizesForCut, findProductForColor, roomForColor, useShop } from '../ShopState';
import { ProductSpec, productSpecLine } from '../ProductSpec';
import { SizeChart } from '../SizeChart';
import { COLOR_META } from '../colors';
import type { ShirtCut } from '../../types';
import { groszeToZl, formatGroszeAsZl } from '../../util/money';

export function Main() {
  const navigate = useNavigate();
  const {
    selection,
    setSelection,
    catalog,
    cart,
    addToCart,
    removeFromCart,
    cartTotalQty,
    cartTotalGrosze,
    verification,
  } = useShop();

  if (catalog.status !== 'ready') {
    return <div style={{ padding: 24, textAlign: 'center', fontSize: 13 }}>Wczytywanie…</div>;
  }
  const { products } = catalog;
  const product = findProductForColor(products, selection.color);
  if (!product) {
    return (
      <div style={{ padding: 24, textAlign: 'center', fontSize: 13 }}>
        Wybrany kolor jest niedostępny.
      </div>
    );
  }
  // The router sends an unverified visitor back to the gate; this only narrows the type.
  if (!verification) return null;
  const sizes = availableSizesForCut(product, selection.cut);
  const specLine = productSpecLine(product);
  const description = product.description.trim();
  const priceZl = groszeToZl(product.price_grosze);
  const draftSubtotalGrosze = product.price_grosze * selection.qty;
  const colorLabel = COLOR_META[selection.color].label;
  // Capped by what this external order has left of the selected colour, not by a global quota: a
  // second shirt on one ticket has to be the other colour, and earlier orders spend the same share.
  const room = roomForColor(verification, cart, selection.color);
  const canAdd = selection.qty > 0 && selection.qty <= room.left;
  const addLabel = (() => {
    if (room.leftOverall <= 0) return 'LIMIT OSIĄGNIĘTY';
    if (room.left <= 0) return 'LIMIT W TYM KOLORZE';
    if (selection.qty > room.left) return `MAX ${room.left} SZT W TYM KOLORZE`;
    return `DODAJ · ${formatGroszeAsZl(draftSubtotalGrosze).replace(' zł', ' ZŁ')}`;
  })();
  const roomLine =
    `${colorLabel}: ` +
    (room.alreadyOrdered > 0
      ? `ZAMÓWIONO JUŻ ${room.alreadyOrdered} Z ${room.allowance}`
      : `MAX ${room.allowance} SZT NA TYM ZAMÓWIENIU`) +
    ` · POZOSTAŁO ${Math.max(0, room.left)}`;

  const pickCut = (c: ShirtCut) => {
    const next = availableSizesForCut(product, c);
    const fallback = next.includes(selection.size) ? selection.size : next[0] ?? 'M';
    setSelection({ cut: c, size: fallback });
  };

  const handleAddToCart = () => {
    if (!canAdd) return;
    addToCart({
      productId: product.id,
      color: selection.color,
      cut: selection.cut,
      size: selection.size,
      qty: selection.qty,
      unitPriceGrosze: product.price_grosze,
    });
    setSelection({ qty: 1 });
  };

  return (
    <div className="phone-screen">
      <div
        style={{
          position: 'relative',
          zIndex: 5,
          padding: '12px 18px 0',
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          color: 'var(--shop-fg)',
        }}
        className="mono"
      >
        <span>9:41</span>
        <span>●●●●● ⌬</span>
      </div>
      <Stripe>OPERACJA TENERYFA · INICJATYWA FANOWSKA</Stripe>

      <div className="scroll" style={{ minHeight: 'calc(100% - 30px)', position: 'relative', zIndex: 5 }}>
        <div style={{ padding: '24px 22px 8px', textAlign: 'center' }}>
          <Emblem size={148} style={{ margin: '0 auto' }} />
          <div
            className="mono"
            style={{ fontSize: 10, letterSpacing: '.2em', color: 'var(--orange)', marginTop: 10 }}
          >
            KONCERT · 28.11.2026 · TENERYFA
          </div>
        </div>

        <div
          style={{
            position: 'relative',
            margin: '4px auto 0',
            width: 280,
            height: 285,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ width: 240, height: 265 }}>
            {/* Keyed on the product: switching color swaps the whole photo set, and a
                surviving scroll offset would land the buyer mid-strip on slide three. */}
            <ProductGallery
              key={product.id}
              images={product.images}
              alt={product.name}
              fallback={<Shirt color={selection.color} cut={selection.cut} />}
            />
          </div>
        </div>

        <div style={{ padding: '14px 22px 18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 10 }}>
            <div className="display" style={{ fontSize: 32 }}>
              KOSZULKA
            </div>
            <div className="display" style={{ fontSize: 30, color: 'var(--orange)' }}>
              {priceZl} ZŁ
            </div>
          </div>
          {specLine && (
            <div
              className="mono"
              style={{
                fontSize: 11,
                color: 'rgba(var(--shop-muted-rgb),.55)',
                letterSpacing: '.1em',
              }}
            >
              {specLine}
            </div>
          )}
          {description && (
            <p style={{ marginTop: 6, marginBottom: 0, fontSize: 12.5, lineHeight: 1.5, color: 'rgba(var(--shop-muted-rgb),.7)', whiteSpace: 'pre-line' }}>
              {description}
            </p>
          )}
          <div style={{ height: 14 }} />

          <div className="display" style={{ fontSize: 12, letterSpacing: '.14em', marginBottom: 8 }}>
            KOLOR
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
            {products.map((p) => {
              const meta = COLOR_META[p.color];
              const active = p.color === selection.color;
              return (
                <button
                  key={p.color}
                  onClick={() => setSelection({ color: p.color })}
                  style={{
                    padding: '12px 14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    background: active ? 'var(--orange)' : 'transparent',
                    color: active ? 'var(--ink)' : 'var(--shop-fg)',
                    border: active ? '2px solid var(--orange)' : '2px solid var(--shop-fg)',
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      background: meta.swatch,
                      border: '1.5px solid currentColor',
                      display: 'inline-block',
                    }}
                  />
                  <span className="display" style={{ fontSize: 16 }}>
                    {meta.label}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="display" style={{ fontSize: 12, letterSpacing: '.14em', marginBottom: 8 }}>
            KRÓJ
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
            {(['męski', 'damski'] as const).map((c) => {
              const active = c === selection.cut;
              return (
                <button
                  key={c}
                  onClick={() => pickCut(c)}
                  style={{
                    padding: '12px 0',
                    cursor: 'pointer',
                    background: active ? 'var(--orange)' : 'transparent',
                    color: active ? 'var(--ink)' : 'var(--shop-fg)',
                    border: active ? '2px solid var(--orange)' : '2px solid var(--shop-fg)',
                    fontFamily: 'var(--display)',
                    fontSize: 16,
                    textTransform: 'uppercase',
                    letterSpacing: '.06em',
                  }}
                >
                  {c}
                </button>
              );
            })}
          </div>

          <div className="display" style={{ fontSize: 12, letterSpacing: '.14em', marginBottom: 8 }}>
            ROZMIAR
          </div>
          <div
            style={{
              display: 'grid',
              // A ninth button would leave each one narrower than "XXL" on a phone.
              gridTemplateColumns: `repeat(${sizes.length > 6 ? Math.ceil(sizes.length / 2) : sizes.length},1fr)`,
              gap: 6,
              marginBottom: 10,
            }}
          >
            {sizes.map((s) => {
              const active = s === selection.size;
              return (
                <button
                  key={s}
                  onClick={() => setSelection({ size: s })}
                  style={{
                    padding: '10px 0',
                    cursor: 'pointer',
                    background: active ? 'var(--orange)' : 'transparent',
                    color: active ? 'var(--ink)' : 'var(--shop-fg)',
                    border: active ? '2px solid var(--orange)' : '2px solid var(--shop-fg)',
                    fontFamily: 'var(--display)',
                    fontSize: 16,
                  }}
                >
                  {s}
                </button>
              );
            })}
          </div>
          <div style={{ marginBottom: 16 }}>
            <SizeChart cut={selection.cut} sizes={sizes} selected={selection.size} fontSize={11} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div className="display" style={{ fontSize: 12, letterSpacing: '.14em' }}>
              ILOŚĆ
            </div>
            <div style={{ display: 'flex', alignItems: 'center', border: '2px solid var(--shop-fg)' }}>
              <button onClick={() => setSelection({ qty: Math.max(1, selection.qty - 1) })} style={qtyBtn} aria-label="Mniej">
                −
              </button>
              <span className="display" style={{ fontSize: 20, minWidth: 32, textAlign: 'center' }}>
                {selection.qty}
              </span>
              <button
                onClick={() => setSelection({ qty: selection.qty + 1 })}
                style={qtyBtn}
                aria-label="Więcej"
                disabled={selection.qty >= room.left}
              >
                +
              </button>
            </div>
          </div>

          <div
            className="mono"
            style={{
              marginTop: 8,
              fontSize: 10,
              letterSpacing: '.08em',
              color: 'rgba(var(--shop-muted-rgb),.55)',
            }}
          >
            {roomLine}
          </div>

          <button
            className="btn full"
            onClick={handleAddToCart}
            disabled={!canAdd}
            style={{ opacity: canAdd ? 1 : 0.4, cursor: canAdd ? 'pointer' : 'not-allowed' }}
          >
            {addLabel}
          </button>

          <div
            style={{
              marginTop: 14,
              border: '2px solid var(--shop-fg)',
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div className="display" style={{ fontSize: 12, letterSpacing: '.12em' }}>
                TWÓJ KOSZYK
              </div>
              <div className="mono" style={{ fontSize: 10, color: 'rgba(var(--shop-muted-rgb),.6)', letterSpacing: '.12em' }}>
                {cartTotalQty}/{verification.remaining}
              </div>
            </div>

            {cart.length === 0 ? (
              <div className="mono" style={{ fontSize: 10, color: 'rgba(var(--shop-muted-rgb),.55)', letterSpacing: '.06em' }}>
                KOSZYK PUSTY · DODAJ KOSZULKĘ
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {cart.map((row) => {
                  const meta = COLOR_META[row.color];
                  const rowSubtotal = row.unitPriceGrosze * row.qty;
                  return (
                    <div
                      key={row.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr auto auto',
                        gap: 8,
                        alignItems: 'center',
                        padding: '6px 8px',
                        border: '1px dashed rgba(var(--shop-muted-rgb),.25)',
                      }}
                    >
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          background: meta.swatch,
                          border: '1.5px solid var(--shop-fg)',
                          display: 'inline-block',
                        }}
                      />
                      <div style={{ minWidth: 0 }}>
                        <span className="mono" style={{ fontSize: 10.5, letterSpacing: '.04em' }}>
                          {meta.label} · {row.cut.toUpperCase()} · {row.size} × {row.qty}
                        </span>
                        <ProductSpec
                          product={products.find((p) => p.id === row.productId)}
                          fontSize={9.5}
                        />
                      </div>
                      <span className="mono" style={{ fontSize: 10.5 }}>
                        {formatGroszeAsZl(rowSubtotal)}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFromCart(row.id)}
                        aria-label="Usuń pozycję"
                        style={{
                          width: 22,
                          height: 22,
                          background: 'transparent',
                          border: '1px solid rgba(var(--shop-muted-rgb),.4)',
                          color: 'var(--shop-fg)',
                          cursor: 'pointer',
                          fontSize: 11,
                          lineHeight: 1,
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <button
              className="btn full"
              onClick={() => navigate('/sklep/checkout')}
              disabled={cart.length === 0}
              style={{
                fontSize: 14,
                padding: '12px 16px',
                opacity: cart.length === 0 ? 0.4 : 1,
                cursor: cart.length === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              DO KASY · {formatGroszeAsZl(cartTotalGrosze).replace(' zł', ' ZŁ')} →
            </button>
          </div>

          <div style={{ display: 'none' }}>{priceZl}</div>
        </div>
      </div>
    </div>
  );
}

const qtyBtn = {
  width: 36,
  height: 36,
  background: 'transparent',
  border: 0,
  color: 'var(--shop-fg)',
  fontFamily: 'var(--display)',
  fontSize: 22,
  cursor: 'pointer',
} as const;
